import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring } from "framer-motion";

const base =
  "group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full px-8 py-4 text-sm font-semibold tracking-wide transition-shadow duration-300";

const variants = {
  primary:
    "bg-gradient-to-r from-[#1C7543] to-[#D3B74D] text-white shadow-[0_10px_36px_rgba(28,117,67,0.4)] hover:shadow-[0_10px_48px_rgba(211,183,77,0.55)]",
  ghost:
    "border border-white/20 bg-white/5 text-white backdrop-blur-md hover:bg-white/10",
  ghostLight:
    "border border-[#07130C]/15 bg-transparent text-[#07130C] hover:bg-[#07130C]/5",
};

export const Button = ({
  variant = "primary",
  to,
  href,
  type = "button",
  onClick,
  children,
  className = "",
  magnetic = true,
  ...rest
}) => {
  const wrapRef = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 220, damping: 16, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 220, damping: 16, mass: 0.4 });

  const onMove = (e) => {
    if (!magnetic || !wrapRef.current) return;
    if (window.matchMedia("(pointer: coarse)").matches) return;
    const r = wrapRef.current.getBoundingClientRect();
    x.set((e.clientX - r.left - r.width / 2) * 0.18);
    y.set((e.clientY - r.top - r.height / 2) * 0.28);
  };
  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  const cls = `${base} ${variants[variant]} ${className}`;
  const inner = (
    <>
      <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" />
      <span className="relative z-10 inline-flex items-center gap-2">{children}</span>
    </>
  );

  return (
    <motion.span
      ref={wrapRef}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ x: sx, y: sy }}
      className="inline-block"
    >
      {to ? (
        <Link to={to} className={cls} {...rest}>
          {inner}
        </Link>
      ) : href ? (
        <a href={href} className={cls} {...rest}>
          {inner}
        </a>
      ) : (
        <button type={type} onClick={onClick} className={cls} {...rest}>
          {inner}
        </button>
      )}
    </motion.span>
  );
};
